/**
 * Practice (docs/spec/07 §2): any cave of Classic, The Deep or the player's own, at any level, with
 * unlimited lives; best scores per cave and level. Returns here after each run.
 */

import { CLASSIC, DEEP, playOrder } from '../../caves';
import { practiceKey } from '../../platform/records';
import type { Screen } from '../../render/screen';
import type { MenuAction, PointerInput } from '../scene';
import { UiScene } from '../kit/base';
import { CONTENT_TOP, drawButton } from '../kit/chrome';
import { inRect } from '../kit/menuModel';
import type { Rect } from '../kit/menuModel';
import { drawTabs, tabAt, tabRects } from '../kit/tabs';
import type { PracticeState } from './practiceModel';
import { GRID_COLS, GRID_ROWS, PRACTICE_TABS, practiceNav, START_STATE, withScroll } from './practiceModel';
import type { PracticeEntry } from './practiceView';
import {
  cellRect, drawDetails, drawEmptyLibrary, drawGrid, drawLevelRow, KIT_RECT, levelChip, PLAY_RECT,
} from './practiceView';

/** Where the player was, for the page session (Practice returns here after every run). */
let memory: PracticeState = START_STATE;

const TABS: readonly Rect[] = tabRects(PRACTICE_TABS.map((t) => t.label), CONTENT_TOP - 3);
const LEVEL_ROW: Rect = { x: 0, y: 170, w: 160, h: 18 };

export class PracticeScene extends UiScene {
  protected readonly heading = 'PRACTICE';
  private state = memory;
  private entries: PracticeEntry[] = [];
  private bests = new Map<string, number>();
  private hotCell = -1;
  private hotTab = -1;

  override enter(): void {
    this.load();
    super.enter();
  }

  /** The caves of the current tab, and their bests at the current level. */
  private load(): void {
    const tab = PRACTICE_TABS[this.state.tab]?.id ?? 'classic';
    if (tab === 'mine') {
      const mine = this.services.library.list();
      this.entries = mine.map((c) => ({ def: c.def, key: `lib|${c.key}|${c.saved}`, badge: '' }));
    } else {
      this.entries = playOrder(tab === 'deep' ? DEEP : CLASSIC).map((def) => ({ def, badge: def.id }));
    }
    const index = Math.min(this.state.index, Math.max(0, this.entries.length - 1));
    this.set({ ...this.state, index });
    this.bests.clear();
  }

  private set(s: PracticeState): void {
    this.state = withScroll(s, this.entries.length);
    memory = this.state;
  }

  private best(e: PracticeEntry): number {
    const k = practiceKey(e.def, this.state.level);
    let b = this.bests.get(k);
    if (b === undefined) {
      b = this.services.records.practiceBest(k);
      this.bests.set(k, b);
    }
    return b;
  }

  private get current(): PracticeEntry | undefined {
    return this.entries[this.state.index];
  }

  protected override describe(): string {
    const tab = PRACTICE_TABS[this.state.tab]?.label ?? '';
    const e = this.current;
    return `${this.heading}. ${tab}. ${e ? `${e.def.name}, level ${this.state.level}` : 'No caves yet'}`;
  }

  protected override hint(): string {
    return `▲▼◄► CHOOSE   ${this.words().ok} PLAY   ${this.words().back} BACK`;
  }

  protected paint(screen: Screen): void {
    const s = this.state;
    drawTabs(screen, PRACTICE_TABS.map((t) => t.label), s.tab, TABS, s.focus === 'tabs', this.ms, this.hotTab);
    const e = this.current;
    if (!e) {
      drawEmptyLibrary(screen);
      drawButton(screen, KIT_RECT, 'CONSTRUCTION KIT', s.focus === 'grid' ? 'selected' : 'idle', this.ms);
    } else {
      const best = (x: PracticeEntry): number => this.best(x);
      drawGrid(screen, this.entries, s.scroll, s.index, s.focus === 'grid', this.hotCell, s.level, best, this.ms, 2);
      this.paintScrollMarks(screen);
      drawDetails(screen, e, this.best(e), s.level);
    }
    drawLevelRow(screen, s.level, s.focus === 'level', this.ms);
    drawButton(screen, PLAY_RECT, 'PLAY', e ? (s.focus === 'play' ? 'selected' : 'idle') : 'disabled', this.ms);
  }

  private paintScrollMarks(screen: Screen): void {
    const rows = Math.ceil(this.entries.length / GRID_COLS);
    if (this.state.scroll > 0) screen.text('▲', 312, 38, 12);
    if (this.state.scroll + GRID_ROWS < rows) screen.text('▼', 312, 132, 12);
  }

  protected onAction(a: MenuAction): void {
    const s = this.state;
    if (this.entries.length === 0 && s.focus === 'grid' && a === 'confirm') {
      this.sound('select');
      return this.host.go({ to: 'kit' });
    }
    const { state, effect } = practiceNav(s, a, this.entries.length);
    if (effect === 'blocked') return this.sound('error');
    if (effect === 'play') return this.play();
    if (effect === 'none') return;
    this.set(state);
    if (effect === 'tab') this.load();
    this.sound('move');
    this.say(this.describe());
  }

  private play(): void {
    const e = this.current;
    if (!e) return this.sound('error');
    this.sound('select');
    const level = this.state.level;
    this.host.go({ to: 'play', request: { mode: 'practice', def: e.def, level, back: { to: 'practice' } } });
  }

  protected override onPointer(p: PointerInput): void {
    const s = this.state;
    if (p.kind === 'wheel') return this.onAction(p.delta > 0 ? 'down' : 'up');
    const tab = tabAt(TABS, p.x, p.y);
    const slot = this.slotAt(p.x, p.y);
    if (p.kind === 'move') {
      this.hotTab = tab;
      this.hotCell = slot;
      return;
    }
    if (p.kind !== 'down' || p.button !== 0) return;
    if (tab >= 0) {
      if (tab !== s.tab) {
        this.set({ ...s, tab, index: 0, scroll: 0, focus: 'grid' });
        this.load();
        this.sound('move');
        this.say(this.describe());
      }
      return;
    }
    if (this.entries.length === 0 && inRect(KIT_RECT, p.x, p.y)) return this.onAction('confirm');
    if (slot >= 0) {
      if (slot === s.index && s.focus === 'grid') return this.play();
      this.set({ ...s, index: slot, focus: 'grid' });
      this.sound('move');
      return this.say(this.describe());
    }
    const chip = [0, 1, 2, 3, 4].find((i) => inRect(levelChip(i), p.x, p.y));
    if (chip !== undefined) {
      this.set({ ...s, level: chip + 1, focus: 'level' });
      return this.sound('move');
    }
    if (inRect(LEVEL_ROW, p.x, p.y)) return this.set({ ...s, focus: 'level' });
    if (inRect(PLAY_RECT, p.x, p.y)) this.play();
  }

  /** The cave index under a point, or -1. */
  private slotAt(x: number, y: number): number {
    for (let slot = 0; slot < GRID_COLS * GRID_ROWS; slot++) {
      const i = this.state.scroll * GRID_COLS + slot;
      if (i >= this.entries.length) break;
      if (inRect(cellRect(slot), x, y)) return i;
    }
    return -1;
  }
}
